import React, { useState, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import NewMessageModal from './NewMessageModal';
import ContactBook from './ContactBook';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faComment, faAddressBook } from '@fortawesome/free-regular-svg-icons'
import { faUsers, faUsersSlash, faUserFriends, faUser } from '@fortawesome/free-solid-svg-icons'
import { Popover, PopoverBody } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import { useIpcBadgeUpdate } from '../../../hooks/useIpcBadgeUpdate';

const Sessions = ({ 
  onSelectChat, 
  fromIndex,
  selectedChat, 
  newWebSocketMessage, 
  contacts, 
  getDisplayName,
  username,
  password,
  cloudId,
  dids,
  hasContact,
  showNewMessageModal,
  setShowNewMessageModal,
  initialRecipient,
  setInitialRecipient,
  initialSessionId,
  setInitialSessionId,
  ipcManager,
  ipcInitialized
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showContactBook, setShowContactBook] = useState(false);
  const [conversations, setConversations] = useState([]); // Keep original conversations state
  const [allSessions, setAllSessions] = useState([]); // Store all sessions
  const [renderedSessions, setRenderedSessions] = useState([]); // Store currently rendered sessions
  const BATCH_SIZE = 20; // Number of sessions to load at once
  const sessionsContainerRef = useRef(null); // Reference to the sessions container
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Add new state for menu control
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [showMenu, setShowMenu] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const longPressTimer = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredChatId, setHoveredChatId] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(null);
  const [sharedPopoverOpen, setSharedPopoverOpen] = useState(null);
  const popoverTimeoutRef = useRef(null);
  const sharedPopoverTimeoutRef = useRef(null);

  const [selectedDid, setSelectedDid] = useState('');
  //const [initialRecipient, setInitialRecipient] = useState(null);
  
  // Initialize badge update hook
  const { updateBadgeCount } = useIpcBadgeUpdate(ipcManager, ipcInitialized);
  const initialBadgeCleared = useRef(false);
  const initialSessionIdRef = useRef(initialSessionId);

  useEffect(() => {
    initialSessionIdRef.current = initialSessionId;
  }, [initialSessionId]);
  
  // Calculate unread count
  const unreadCount = useMemo(() => {
    return conversations.filter(conv => conv.unread).length;
  }, [conversations]);
  
  // Clear badge once when sessions finish loading so unread sync does not restore it on open.
  // After that first clear, continue syncing badge from unread count.
  useEffect(() => {
    if (loading) return;
    if (!initialBadgeCleared.current) {
      updateBadgeCount(0);
      initialBadgeCleared.current = true;
      return;
    }
    updateBadgeCount(unreadCount);
  }, [loading, unreadCount, updateBadgeCount]);
  
  // Check if device is mobile
  // Load more sessions when user scrolls
  const loadMoreSessions = () => {
    if (isLoadingMore || renderedSessions.length >= conversations.length) return;
    
    setIsLoadingMore(true);
    const nextBatch = conversations.slice(
      renderedSessions.length,
      renderedSessions.length + BATCH_SIZE
    );
    
    setRenderedSessions(prev => [...prev, ...nextBatch]);
    setIsLoadingMore(false);
  };

  // Handle scroll event
  const handleScroll = (e) => {
    const container = e.target;
    if (container.scrollHeight - container.scrollTop <= container.clientHeight + 100) {
      loadMoreSessions();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const myIsMobile = window.innerWidth < 768
      setIsMobile(myIsMobile);
      getSessions(myIsMobile);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest('.menu-trigger') && !event.target.closest('.context-menu')) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  // Handle long press
  const handleTouchStart = (e, chat) => {
    e.preventDefault(); // Prevent default touch behavior
    longPressTimer.current = setTimeout(() => {
      const rect = e.target.closest('.conversation-item').getBoundingClientRect();
      setMenuPosition({ x: rect.right - 150, y: rect.top + 10 }); // Position menu at top-right
      setSelectedItemId(chat.id);
      setShowMenu(true);
    }, 500); // 500ms for long press
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  // Handle menu toggle for desktop
  const handleMenuToggle = (e, chat) => {
    e.stopPropagation(); // Prevent chat selection
    const rect = e.currentTarget.closest('.conversation-item').getBoundingClientRect();
    setMenuPosition({ x: rect.right - 150, y: rect.top + 10 }); // Position menu at top-right
    setSelectedItemId(chat.id);
    setShowMenu(!showMenu || selectedItemId !== chat.id);
  };

  // Handle delete
  const handleDelete = async (chatId) => {
    // Find the chat in conversations
    const chat = conversations.find(conv => conv.id === chatId);
    
    // Check if it's a shared session and user is not the primary user
    if (chat?.isShared && chat.primaryUser !== username) {
      toast.error("Only the primary user can delete this thread");
      setShowMenu(false);
      return;
    }

    try {
      const payLoad = {
        username,
        password,
        cloud_id: cloudId,
        session_id: chatId
      };
      
      await axios.post(`${process.env.REACT_APP_MESSAGE_API_URL}/deleteMsgOrSessiononNS`, payLoad);
      
      // Remove the conversation from all session lists used by the UI
      const removeSession = (sessions) => sessions.filter(conv => conv.id !== chatId);
      setConversations(removeSession);
      setAllSessions(removeSession);
      setRenderedSessions(removeSession);
      
      // If the deleted chat was selected, clear selection
      if (selectedChat?.id === chatId) {
        onSelectChat(null);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
    setShowMenu(false);
  };

  useEffect(() => {
    if(selectedChat){
      // mark this chat as read
      // const payLoad = {
      //   username: username,
      //   password: password,
      //   cloud_id: cloudId,
      //   session_id: selectedChat.id
      // }
      // axios.post(`${process.env.REACT_APP_MESSAGE_API_URL}/markAsRead`, payLoad)
      // .then(response => {
        // update the conversation to have unread set to false
        const updatedConversations = [...conversations];
        const conversationIndex = updatedConversations.findIndex(
          conv => conv.id === selectedChat.id
        );
        //console.log("Found conversation index: " + conversationIndex);
        if (conversationIndex !== -1 && updatedConversations[conversationIndex].unread) {
          // Mark as read — unread flag drives list UI styling
          updatedConversations[conversationIndex].unread = false;
          setConversations(updatedConversations);
        }
      // })
      // .catch(error => {
      //   console.log(error); 
      // })
    }
  }, [selectedChat]);

  const formatMessageTime = (timestamp) => {
    const messageTime = moment.utc(timestamp).local();
    const now = moment();
    
    if (messageTime.isSame(now, 'day')) {
      // If it's today, show time in 12-hour format
      return messageTime.format('h:mm A');
    } else if (messageTime.isSame(now.clone().subtract(1, 'day'), 'day')) {
      // If it's yesterday
      return 'Yesterday';
    } else if (messageTime.isSame(now, 'week')) {
      // If it's within this week, show day name
      return messageTime.format('dddd');
    } else {
      // If it's older than a week, show date
      return messageTime.format('MM/DD/YY');
    }
  };

  // Handle new WebSocket messages
  useEffect(() => {
    if (newWebSocketMessage) {
      //console.log("New WebSocket message received: " + newWebSocketMessage);
      // Update conversations with the new message
      setConversations(prevConversations => {
        const updatedConversations = [...prevConversations];
        
        // Find the conversation with matching SessionID
        const conversationIndex = updatedConversations.findIndex(
          conv => conv.id === newWebSocketMessage.SessionID
        );
        
        if (conversationIndex !== -1) {
          // Remove the conversation from its current position
          const [updatedConversation] = updatedConversations.splice(conversationIndex, 1);
          
          // Update the conversation with new message data
          updatedConversation.lastMessage = newWebSocketMessage.Message;
          updatedConversation.timestamp = moment.utc().toISOString();
          updatedConversation.time = formatMessageTime(updatedConversation.timestamp);
          updatedConversation.unread = true;
          
          // Add the updated conversation to the top of the list
          updatedConversations.unshift(updatedConversation);
        } else {
          // Create a new conversation if it doesn't exist
          const isGroup = newWebSocketMessage.TermUID.startsWith('[');

          const newConversation = {
            id: newWebSocketMessage.SessionID,
            name: newWebSocketMessage.FromUID === username ? newWebSocketMessage.TermUID : newWebSocketMessage.FromUID,
            phone: "",
            lastMessage: newWebSocketMessage.Message,
            timestamp: moment.utc().toISOString(),
            time: formatMessageTime(moment.utc().toISOString()),
            isGroup: isGroup,
            unread: newWebSocketMessage.SessionID !== selectedChat?.id
          };
          // Add the new conversation to the top of the list
          updatedConversations.unshift(newConversation);
        }
        
        return updatedConversations;
      });
      
      // If this message belongs to the currently selected chat, update the Messages component
      if (selectedChat && newWebSocketMessage.SessionID === selectedChat.id) {
        // Find the updated chat in the current conversations state
        const updatedChat = conversations.find(conv => conv.id === newWebSocketMessage.SessionID);
        if (updatedChat && onSelectChat) {
          onSelectChat(updatedChat);
        }
      }
    }
  }, [newWebSocketMessage]);

  const getSessions = (myIsMobile) => {
    const payLoad = {
      username: username,
      password: password,
      cloud_id: cloudId
    }
    axios.post(`${process.env.REACT_APP_MESSAGE_API_URL}/getSessions`, payLoad)
    .then(response => {
      const myConversations = [];
        response.data.map(session => {
          // Check if it's a group message by seeing if remote is an array
          const isGroup = session.remote.startsWith('[');
          
          myConversations.push({
            id: session.session_id,
            name: session.remote,
            phone: "",
            myPhone: session.smsani,
            lastMessage: session.last_mesg,
            timestamp: session.last_timestamp, // Add timestamp to object for sorting
            time: formatMessageTime(session.last_timestamp),
            isGroup: isGroup,
            unread: session.last_status !== "read",
            isShared: session.shared_message?.shared_did_flag === "true",
            sharedUsers: session.shared_message?.secondary_user || [],
            sharedDid: session.shared_message?.shared_did || "",
            primaryUser: session.shared_message?.primary_user || "",
          })
        })

        // Sort conversations by timestamp in descending order
        myConversations.sort((a, b) => moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf());
        
        // Update all states
        setConversations(myConversations); // Keep original conversations state in sync
        setAllSessions(myConversations);   // Store all sessions for virtual scrolling
        
        // Initialize rendered sessions with first batch
        let initialBatch = myConversations.slice(0, BATCH_SIZE);

        // Open session from URL session_id if present
        const sessionIdToOpen = initialSessionIdRef.current;
        if (sessionIdToOpen) {
          const targetChat = myConversations.find(
            conv => String(conv.id) === String(sessionIdToOpen)
          );
          if (targetChat) {
            if (!initialBatch.some(conv => String(conv.id) === String(targetChat.id))) {
              initialBatch = [targetChat, ...initialBatch.slice(0, BATCH_SIZE - 1)];
            }
            setRenderedSessions(initialBatch);
            onSelectChat(targetChat);
          } else {
            setRenderedSessions(initialBatch);
            onSelectChat({ id: sessionIdToOpen, name: '', phone: '', sharedUsers: [] });
          }
          initialSessionIdRef.current = null;
          if (setInitialSessionId) {
            setInitialSessionId(null);
          }
        } else {
          setRenderedSessions(initialBatch);
          // Only select first chat if not on mobile
          if (!myIsMobile && initialBatch.length > 0) {
            onSelectChat(initialBatch[0]);
          }
        }
        
        setLoading(false);
    })
    .catch(error => {
      console.log(error);
      setLoading(false);
    })
  }

  const handleSendNewMessage = async (recipients, message, file) => {
    // Implement the API call to send a new message
    let smsTo = "";
    if(recipients.length === 1){
      smsTo = recipients[0].split('@')[0];
    } else {
      smsTo = recipients.map(recipient => recipient.split('@')[0]).join(',');
    }
    // Create FormData object
    const formData = new FormData();
    formData.append('from', username);
    if (selectedDid.did && !areAllRecipientsInternal(recipients)) {
      formData.append('shared_message_flag', true);
      formData.append('did', selectedDid.did);
      formData.append('primary_user', selectedDid.primary_user);
    } else if (selectedDid.number) {
      formData.append('did', selectedDid.number);
    }
    formData.append('password', password);
    formData.append('sms_to', smsTo);
    formData.append('cloud_id', cloudId);
    formData.append('media_attached', file ? 'true' : 'false');
    formData.append('sms_body', message);

    if (file) {
      formData.append('files', file);
    }

    try {
      // Example API call with FormData
      const response = await axios.post(`${process.env.REACT_APP_MESSAGE_API_URL}/sendMessage`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      //console.log("Message sent:", response.data);
      // Refresh sessions to show the new conversation
      getSessions(isMobile);
      return response.data;
    } catch (error) {
      console.error("Error sending message:", error);
      throw error; // Re-throw the error so the modal can handle it
    }
  };

  // Filter conversations based on search query
  const filteredConversations = useMemo(() => {
    // When searching, we search through all conversations
    const sourceSessions = searchQuery.trim() ? conversations : renderedSessions;
    
    if (!searchQuery.trim()) return sourceSessions;
    
    const query = searchQuery.toLowerCase();
    return sourceSessions.filter(chat => 
      chat.name.toLowerCase().includes(query) ||
      getDisplayName(chat.name)?.toLowerCase().includes(query) ||
      chat.phone.toLowerCase().includes(query) ||
      chat.lastMessage.toLowerCase().includes(query)
    );
  }, [searchQuery, conversations, renderedSessions]);

  // Add the ContextMenu component
  const ContextMenu = ({ x, y, onDelete }) => (
    <div 
      className="context-menu absolute bg-white shadow-lg rounded-lg py-2 z-50"
      style={{ 
        left: `${x}px`, 
        top: `${y}px`,
        minWidth: '150px'
      }}
    >
      <button
        onClick={onDelete}
        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
        Delete Chat
      </button>
    </div>
  );

  const isNumeric = (str) => {
    return /^\d+$/.test(str); // Check if the part before @ is numeric
  };

  const getAvatarDisplayName = (name) => {
    //console.log("NAME IS ", name);
    return isNumeric(name) ? '#' : getDisplayName(name);
  };

  const renderAvatar = (chat) => {
    if (chat.isGroup) {
      return (
        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-200">
          <FontAwesomeIcon 
            icon={faUsers}
            className="text-black"
            style={{ fontSize: "24px" }}
          />
        </div>
      );
    }
    
    const avatarName = getAvatarDisplayName(chat.name);
    if (avatarName === '#') {
      return (
        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-200">
          <FontAwesomeIcon 
            icon={faUser}
            className="text-black"
            style={{ fontSize: "24px" }}
          />
        </div>
      );
    }
    
    return (
      <img
        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(avatarName)}`}
        alt={avatarName}
        className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
      />
    );
  };

  // Function to get group members list
  const getGroupMembers = (chatName) => {
    if (chatName.startsWith('[')) {
      try {
        const members = JSON.parse(chatName);
        return members.map(member => ({
          id: member,
          name: contacts[member.toLowerCase()] || member
        }));
      } catch (error) {
        console.error('Error parsing group members:', error);
        return [];
      }
    }
    return [];
  };

  // Handle mouse enter for desktop
  const handleMouseEnter = (chatId) => {
    if (!isMobile) {
      if (popoverTimeoutRef.current) {
        clearTimeout(popoverTimeoutRef.current);
      }
      setPopoverOpen(chatId);
    }
  };

  // Handle mouse leave for desktop
  const handleMouseLeave = () => {
    if (!isMobile) {
      popoverTimeoutRef.current = setTimeout(() => {
        setPopoverOpen(null);
      }, 300); // Small delay to prevent flickering
    }
  };

  // Handle click for mobile
  const handleClick = (e, chatId) => {
    if (isMobile) {
      e.stopPropagation();
      setPopoverOpen(popoverOpen === chatId ? null : chatId);
    }
  };

  // Handle shared pill click
  const handleSharedClick = (e, chatId) => {
    e.stopPropagation(); // Prevent chat selection
    setSharedPopoverOpen(sharedPopoverOpen === chatId ? null : chatId);
  };

  // Handle click for group name
  const handleGroupClick = (e, chatId) => {
    e.stopPropagation(); // Prevent chat selection
    setPopoverOpen(popoverOpen === chatId ? null : chatId);
  };

  const handleBack = () => {
    // Get the current URL search params
    const urlParams = new URLSearchParams(window.location.search);
    const params = {};
    
    // Decode each parameter value
    for (const [key, value] of urlParams.entries()) {
      params[key] = decodeURIComponent(value);
    }
    
    // Remove from_index parameter
    delete params.from_index;
    
    // Construct the new URL manually to avoid automatic encoding
    const paramString = Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
      
    // Redirect to index with decoded parameters
    window.location.href = `/index?${paramString}`;
  }

  // Add new function to handle clicks outside popovers
  const handleOutsideClick = (e) => {
    // Check if click is inside a popover or its trigger
    const isPopoverClick = e.target.closest('.popover') || 
                          e.target.closest('[id^="group-"]') ||
                          e.target.closest('[id^="shared-"]');
    
    if (!isPopoverClick) {
      setPopoverOpen(null);
      setSharedPopoverOpen(null);
    }
  };

  const handleSelectRecipient = (phone) => {
    setInitialRecipient(phone);
    setShowNewMessageModal(true);
  };

   // Add function to check if all recipients are internal users
   const areAllRecipientsInternal = (recipients) => {
    return recipients.length > 0 && recipients.every(recipient => recipient.includes('@'));
  };

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden" onClick={handleOutsideClick}>
      {/* Header with New Message Button */}
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-2">
          {
            fromIndex && (
              <span className="cursor-pointer" onClick={handleBack}>
                <i  className="feather icon-arrow-left"> </i>
              </span>
            )
          }
          <h1 className="text-2xl font-semibold text-gray-800 mt-[8px]">Messages</h1>
        </div>
        
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setShowContactBook(true)}
            style={(() => {
              const styles = localStorage.getItem("messaging_styles");
              let buttonStyles = {};
              
              if (styles) {
                try {
                  const parsedStyles = JSON.parse(styles);
                  buttonStyles = {
                    backgroundColor: parsedStyles.message_buttons_color || '#3B82F6',
                    color: parsedStyles.message_buttons_font_color || '#FFFFFF'
                  };
                } catch (e) {
                  console.error('Error parsing messaging styles:', e);
                }
              }
              return buttonStyles;
            })()}
            className={`rounded-lg w-8 h-8 flex items-center justify-center transition-colors duration-200
              ${!localStorage.getItem("messaging_styles") ? 'bg-blue-500 text-white hover:bg-blue-600' : 
              'hover:opacity-80'}`}
          >
            <FontAwesomeIcon 
              icon={faAddressBook}
              style={{ 
                fontSize: "20px",
                color: (() => {
                  const styles = localStorage.getItem("messaging_styles");
                  if (styles) {
                    try {
                      const parsedStyles = JSON.parse(styles);
                      return parsedStyles.message_buttons_font_color || '#FFFFFF';
                    } catch (e) {
                      return '#FFFFFF';
                    }
                  }
                  return '#FFFFFF';
                })()
              }}
            />
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="p-4">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
            className="w-full pr-4 py-2 pl-[40px] bg-gray-100 rounded-full 
                     focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <svg 
            className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
            />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              <svg 
                className="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M6 18L18 6M6 6l12 12" 
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Conversations List */}
      <div 
        ref={sessionsContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto"
      >
        {filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-500">
            <svg 
              className="w-8 h-8 mb-2" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <p>No conversations found</p>
          </div>
        ) : (
          filteredConversations.map((chat, index) => (
            <div
              key={index}
              onTouchStart={(e) => handleTouchStart(e, chat)}
              onTouchEnd={handleTouchEnd}
              onTouchMove={handleTouchEnd}
              onClick={() => !showMenu && onSelectChat(chat)}
              onMouseEnter={() => setHoveredChatId(chat.id)}
              onMouseLeave={() => setHoveredChatId(null)}
              className={`conversation-item flex items-center p-4 cursor-pointer relative
                       transition-colors duration-200 border-b border-gray-200
                       ${selectedChat?.id === chat.id ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
            >
              {/* Avatar */}
              <div className="relative flex flex-col items-center">
                {renderAvatar(chat)}
                {chat.unread && (
                  <span className="absolute -top-2 -right-2 bg-blue-500 text-white 
                                 text-xs rounded-full w-7 h-7 flex items-center font-bold text-[9px]
                                 justify-center">
                    new
                  </span>
                )}
                <p className="text-[10px] text-gray-500 mt-1 mb-0">
                  {chat.name.includes('@') ? 'chat' : 'sms'}
                </p>
              </div>

              {/* Chat Info */}
              <div className="ml-3 flex-1">
                <div className="flex justify-between items-start">
                  <div className="flex items-start">
                    <h3 
                      id={`group-${chat.id}`}
                      onClick={(e) => (chat.isGroup || (getDisplayName(chat.name) !== chat.name)) && handleGroupClick(e, chat.id)}
                      className={`font-semibold text-lg text-gray-800 mb-0 truncate max-w-[150px]
                                ${(chat.isGroup || (getDisplayName(chat.name) !== chat.name)) ? 'hover:text-blue-600 cursor-pointer' : ''}`}
                    >
                      {getDisplayName(chat.name)}
                    </h3>
                    {chat.isGroup && (
                      <Popover
                        placement="bottom"
                        isOpen={popoverOpen === chat.id}
                        target={`group-${chat.id}`}
                        trigger="click"
                      >
                        <PopoverBody onClick={e => e.stopPropagation()}>
                          <div className="py-1">
                            <div className="px-3 py-1 text-sm font-bold text-black border-b border-gray-200">
                              Group Members
                            </div>
                            {getGroupMembers(chat.name).map((member, index) => {
                              const displayName = getDisplayName(member.id);
                              return (
                                <div 
                                  key={index}
                                  className="px-3 py-2 hover:bg-gray-50"
                                >
                                  <div className="font-medium text-gray-800">
                                    {displayName !== member.id ? displayName : member.name}
                                  </div>
                                  <div className="text-sm text-gray-500">{member.id}</div>
                                </div>
                              );
                            })}
                          </div>
                        </PopoverBody>
                      </Popover>
                    )}
                    {!chat.isGroup && getDisplayName(chat.name) !== chat.name && (
                      <Popover
                        placement="bottom"
                        isOpen={popoverOpen === chat.id}
                        target={`group-${chat.id}`}
                        trigger="click"
                      >
                        <PopoverBody onClick={e => e.stopPropagation()}>
                          <div className="py-1">
                            <div className="px-3 py-1 text-sm font-bold text-black border-b border-gray-200">
                              Contact Details
                            </div>
                            <div className="px-3 py-2">
                              <div className="font-medium text-gray-800">{getDisplayName(chat.name)}</div>
                              <div className="text-sm text-gray-500">{chat.name}</div>
                            </div>
                          </div>
                        </PopoverBody>
                      </Popover>
                    )}
                    {chat.isShared && (
                      <>
                        <span 
                          id={`shared-${chat.id}`}
                          onClick={(e) => handleSharedClick(e, chat.id)}
                          className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full cursor-pointer hover:bg-blue-200"
                        >
                          Shared
                        </span>
                        <Popover
                          placement="bottom"
                          isOpen={sharedPopoverOpen === chat.id}
                          target={`shared-${chat.id}`}
                          trigger="click"
                        >
                          <PopoverBody onClick={e => e.stopPropagation()}>
                            <div className="py-1">
                              <div className="px-3 py-1 text-sm font-bold text-black border-b border-gray-200">
                                Shared DID: {chat.sharedDid}
                              </div>
                              <div className="px-3 py-1 text-sm font-bold text-black border-b border-gray-200">
                                Primary User
                              </div>
                              <div className="px-3 py-2 hover:bg-gray-50">
                                <div className="font-medium text-gray-800">{getDisplayName(chat.primaryUser)}</div>
                                <div className="text-sm text-gray-500">{chat.primaryUser}</div>
                              </div>
                              <div className="px-3 py-1 text-sm font-bold text-black border-b border-gray-200">
                                Shared Users
                              </div>
                              {chat.sharedUsers.map((user, index) => {
                                const displayName = getDisplayName(user);
                                return (
                                  <div 
                                    key={index}
                                    className="px-3 py-2 hover:bg-gray-50"
                                  >
                                    <div className="font-medium text-gray-800">{displayName}</div>
                                    <div className="text-sm text-gray-500">{user}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </PopoverBody>
                        </Popover>
                      </>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500">{chat.time}</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mb-[4px]">{chat.myPhone}</p>
                <div className="flex justify-between items-center h-[20px]">
                  <p className="text-sm text-gray-600 truncate max-w-[200px] mb-0">{chat.lastMessage}</p>
                    <button
                      onClick={(e) => handleMenuToggle(e, chat)}
                      className="menu-trigger ml-2 p-1 rounded-full hover:bg-gray-200"
                    >
                      <svg className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="currentColor" style={{ transform: 'rotate(-90deg)' }}>
                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                      </svg>
                    </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <button 
        onClick={() => {
          setShowNewMessageModal(true);
          setInitialRecipient(null);
        }}
        style={(() => {
          const styles = localStorage.getItem("messaging_styles");
          let buttonStyles = {};
          
          if (styles) {
            try {
              const parsedStyles = JSON.parse(styles);
              buttonStyles = {
                backgroundColor: parsedStyles.message_buttons_color || '#3B82F6',
                color: parsedStyles.message_buttons_font_color || '#FFFFFF'
              };
            } catch (e) {
              console.error('Error parsing messaging styles:', e);
            }
          }
          return buttonStyles;
        })()}
        className={`bottom-[40px] fixed left-[calc(100%-150px)] lg:left-[330px] z-10 rounded-lg w-[120px] h-10 flex items-center justify-center transition-colors duration-200
          ${!localStorage.getItem("messaging_styles") ? 'bg-blue-500 text-white hover:bg-blue-600' : 
          'hover:opacity-80'}`}
      >
        <FontAwesomeIcon 
          icon={faComment}
          className=""
          style={{ fontSize: "20px" }}
        />
        <span className="text-sm ml-2">Start chat</span>
      </button>
      {/* Context Menu */}
      {showMenu && (
        <ContextMenu
          x={menuPosition.x}
          y={menuPosition.y}
          onDelete={() => handleDelete(selectedItemId)}
        />
      )}

      {/* New Message Modal */}
      <NewMessageModal 
        dids={dids}
        isOpen={showNewMessageModal}
        onClose={() => {
          setShowNewMessageModal(false);
          setInitialRecipient(null);
        }}
        areAllRecipientsInternal={areAllRecipientsInternal}
        onSend={handleSendNewMessage}
        username={username}
        password={password}
        cloudId={cloudId}
        contacts={contacts}
        setSelectedDid={setSelectedDid}
        selectedDid={selectedDid}
        initialRecipient={initialRecipient}
      />

      {/* Contact Book Modal */}
      <ContactBook
        isOpen={showContactBook}
        onClose={() => setShowContactBook(false)}
        username={username}
        password={password}
        cloudId={cloudId}
        setShowNewMessageModal={setShowNewMessageModal}
        onSelectRecipient={handleSelectRecipient}
      />
      
      <ToastContainer/>
    </div>
  );
};

export default Sessions;
